import React, { useMemo, useState, type PropsWithChildren, type Ref } from 'react';
import noop from 'lodash/noop';
import Button from '@atlaskit/button/standard-button';
import ShareIcon from '@atlaskit/icon/core/migration/share';
import { IconButton } from '@atlaskit/button/new';
import { token } from '@atlaskit/tokens';
import { FabricElementsAnalyticsContext } from '@atlaskit/analytics-namespaced-context';
import ShareIconNew from '@atlaskit/icon/core/share';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	getUserLocation,
	type RouterState,
} from '@atlassian/jira-platform-router-utils/src/common/utils/index.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazyAfterPaint } from '@atlassian/react-loosely-lazy';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	useProjectType,
	useProjectStyle,
} from '@atlassian/jira-project-context-service/src/main.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { createRouterSelector } from '@atlassian/react-resource-router';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { Trigger, usePanel } from '@atlassian/entry-point-panel';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import type {
	LegacyShareClientConfig,
	RenderCustomTriggerButton,
	CustomTriggerButtonProps,
} from './common/types.tsx';
import { messages } from './messages.tsx';
import type { Props } from './ui/index.tsx';
import { shareButtonEntryPoint } from './entrypoint.tsx';

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports, @atlassian/eng-health/no-barrel-files/disallow-reexports, @atlassian/eng-health/no-barrel-files/disallow-reexports
export type { LegacyShareClientConfig, RenderCustomTriggerButton, CustomTriggerButtonProps };

type LoaderProps = {
	triggerButtonStyle?: 'icon-only' | 'icon-with-text';
};

export const ShareLoadingPlaceholder = ({ triggerButtonStyle = 'icon-only' }: LoaderProps) => {
	const { formatMessage } = useIntl();

	return (
		<div>
			<Button
				appearance="subtle"
				iconBefore={
					<ShareIcon
						LEGACY_size="medium"
						spacing="spacious"
						label={formatMessage(messages.shareTriggerButtonText)}
						color={token('color.icon')}
					/>
				}
				isDisabled
			>
				{triggerButtonStyle === 'icon-with-text'
					? formatMessage(messages.shareTriggerButtonText)
					: null}
			</Button>
		</div>
	);
};

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const ShareButtonBase = lazyAfterPaint(
	() => import(/* webpackChunkName: "async-share-button" */ './ui'),
	{ ssr: false },
);

export const useProjectKey = createRouterSelector(
	(state: RouterState) => state.match.params.projectKey || '',
);

const ShareButtonAnalyticsWrapper: React.FunctionComponent<
	PropsWithChildren<{ projectKey?: string }>
> = ({ children, ...props }) => {
	const maybeProjectKey = useProjectKey();
	const projectKey = props.projectKey || maybeProjectKey;
	const projectType = useProjectType(projectKey);
	const projectStyle = useProjectStyle(projectKey);
	const projectContext = useProjectContext();
	const userLocation = getUserLocation();
	const isAdmin = useIsAdmin();
	const isProjectAdmin = isAdmin || projectContext?.data?.isProjectAdmin || false;

	const analyticsData = useMemo(() => {
		const projectContextData = projectKey
			? { projectType, projectStyle, isProjectAdmin }
			: undefined;

		return {
			isAdmin,
			userLocation,
			...projectContextData,
		};
	}, [projectKey, projectType, projectStyle, isProjectAdmin, userLocation, isAdmin]);

	return (
		<ContextualAnalyticsData attributes={analyticsData}>
			<FabricElementsAnalyticsContext data={analyticsData}>
				{children}
			</FabricElementsAnalyticsContext>
		</ContextualAnalyticsData>
	);
};

export const ShareButtonAnalyticsWrapperWithGating = componentWithCondition(
	() => fg('jsw-smart-access-extended-analytics-enabled'),
	ShareButtonAnalyticsWrapper,
	({ children }: PropsWithChildren) => <>{children}</>,
);

export const ShareButton = (props: Props) => (
	<ErrorBoundary id="async.share.button" packageName="share-button">
		<Placeholder
			name="share-button"
			fallback={
				props.renderCustomTriggerButton === undefined ? (
					<ShareLoadingPlaceholder triggerButtonStyle={props.triggerButtonStyle} />
				) : (
					<props.renderCustomTriggerButton isDisabled onClick={noop} />
				)
			}
		>
			<ShareButtonAnalyticsWrapperWithGating>
				<ShareButtonBase {...props} />
			</ShareButtonAnalyticsWrapperWithGating>
		</Placeholder>
	</ErrorBoundary>
);

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export type { Props as ShareButtonProps };

export const ShareButtonAsync = (props: Props) => {
	const { Panel, trigger } = usePanel();
	const entryPointParams = useMemo(() => ({}), []);
	const entryPointProps = useMemo(() => ({ ...props }), [props]);
	const [isOpen, setIsOpen] = useState(false);
	const { formatMessage } = useIntl();

	return (
		<JSErrorBoundary
			id="jsm-share-button-async"
			packageName="jiraShareButton"
			teamName="people-and-teams-collective"
			fallback="unmount"
		>
			<Trigger
				trigger={trigger}
				entryPoint={shareButtonEntryPoint}
				entryPointParams={entryPointParams}
				entryPointProps={entryPointProps}
			>
				{({ ref }) =>
					!isOpen && (
						<IconButton
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							ref={ref as Ref<HTMLButtonElement>}
							testId="share-button.button"
							label={formatMessage(messages.shareTriggerButtonText)}
							appearance="default"
							icon={() => <ShareIconNew spacing="spacious" label="" color={token('color.icon')} />}
							onClick={() => setIsOpen(true)}
						/>
					)
				}
			</Trigger>
			<Panel />
		</JSErrorBoundary>
	);
};
